import * as THREE from "three";
import { BX, CY, G, V3, along, anchors, latheTube, mesh, n, pipe, rod } from "./core";

/** @formigen/geometry — transformer domain generators.
 *  Every dimension is a param from the Design Model; only segment counts are local. */

export function tank(p, mat, M) {
  const g = G();
  const l = n(p.l, 6), w = n(p.w, 3), h = n(p.h, 4);
  const dark = M.tankD || mat;
  g.add(mesh(BX(l, h, w), mat, 0, h / 2, 0));
  g.add(mesh(BX(l + 0.12, n(p.cover_t, 0.06), w + 0.12), mat, 0, h + n(p.cover_t, 0.06) / 2, 0));
  if (p.bell_y != null) g.add(mesh(BX(l + 0.16, 0.12, w + 0.16), dark, 0, p.bell_y, 0));
  if (p.rim_y != null) g.add(mesh(BX(l + 0.08, 0.1, w + 0.08), dark, 0, p.rim_y, 0));
  const st = n(p.stiffener_t, 0.14), sw = n(p.stiffener_w, 0.12), sh = h - 0.25, sy = h / 2 + 0.05;
  for (const s of [1, -1]) for (const x of p.stiffener_x || []) g.add(mesh(BX(st, sh, sw), mat, x, sy, s * (w / 2 + 0.06)));
  for (const s of [1, -1]) for (const z of p.stiffener_z || []) g.add(mesh(BX(sw, sh, st), mat, s * (l / 2 + 0.06), sy, z));
  return anchors(g, { base: [0, 0, 0], top: [0, h, 0], mount: [0, h + n(p.cover_t, 0.06), 0] });
}

const shedGeo = (len, core, big, small, pitch) => {
  const pts = [new THREE.Vector2(0.001, 0), new THREE.Vector2(core, 0)];
  let y = 0.06, i = 0;
  while (y < len - 0.1) {
    const R = i % 2 ? small : big;
    pts.push(new THREE.Vector2(core, y), new THREE.Vector2(R, y + 0.012),
      new THREE.Vector2(R, y + 0.03), new THREE.Vector2(core, y + 0.03 + (R - core) * 0.35));
    y += pitch; i++;
  }
  pts.push(new THREE.Vector2(core, len), new THREE.Vector2(0.001, len));
  return new THREE.LatheGeometry(pts, 28);
};

export function bushing(p, mat, M) {
  const g = G();
  let y = 0;
  const core = n(p.core_r, 0.12);
  if (p.turret) {
    g.add(mesh(CY(n(p.turret_r, 0.3), p.turret, 28), M.tank || mat, 0, p.turret / 2, 0));
    g.add(mesh(CY(n(p.turret_r, 0.3) * 1.12, 0.06, 28), M.tankD || mat, 0, p.turret, 0));
    y = p.turret;
  }
  g.add(mesh(CY(core * 1.9, 0.08, 24), M.metal || mat, 0, y + 0.04, 0));
  g.add(mesh(CY(core * 1.25, n(p.metal, 0.2), 24), M.metal || mat, 0, y + 0.08 + n(p.metal, 0.2) / 2, 0));
  y += 0.08 + n(p.metal, 0.2);
  g.add(mesh(shedGeo(n(p.len, 1), core, n(p.shed_big, core * 1.8), n(p.shed_small, core * 1.5), n(p.pitch, 0.1)), mat, 0, y, 0));
  y += n(p.len, 1);
  g.add(mesh(CY(core * 1.3, 0.14, 24), M.metal || mat, 0, y + 0.07, 0));
  y += 0.14;
  g.add(mesh(CY(core * 0.38, 0.18, 12), M.copper || mat, 0, y + 0.09, 0));
  if (p.ring_r) {
    const t = mesh(new THREE.TorusGeometry(p.ring_r, n(p.ring_t, 0.04), 12, 48), M.metal || mat, 0, y - 0.06, 0);
    t.rotation.x = Math.PI / 2;
    g.add(t);
  }
  return anchors(g, { base: [0, 0, 0], terminal: [0, y + 0.18, 0], tip: [0, y + 0.2, 0] });
}

function coreLimbs(p, mat, limbs) {
  const g = G();
  const yh = n(p.yoke_h, 0.9), lh = n(p.limb_h, 2.5), pitch = n(p.limb_pitch, 2.0);
  const xs = [];
  for (let i = 0; i < limbs; i++) xs.push((i - (limbs - 1) / 2) * pitch);
  for (const x of xs) g.add(mesh(CY(n(p.limb_r, 0.5), lh, n(p.segments, 36)), mat, x, yh / 2 + lh / 2, 0));
  for (const y of [0, yh / 2 + lh + yh / 2])
    g.add(mesh(BX(n(p.yoke_w, pitch * limbs), yh, n(p.yoke_d, 1)), mat, 0, y, 0));
  g.userData.limbs = xs;
  return anchors(g, { base: [0, -yh / 2, 0], top: [0, yh + lh, 0], window: [0, yh / 2, 0] });
}

export const core_3limb = (p, mat) => coreLimbs(p, mat, 3);
export const core_5limb = (p, mat) => coreLimbs(p, mat, 5);

export function winding(p, mat) {
  const g = G();
  g.add(latheTube(n(p.ri, 0.5), n(p.ro, 0.6), 0, n(p.h, 2), mat, 56));
  return anchors(g, { base: [0, 0, 0], top: [0, n(p.h, 2), 0] });
}

export function radiator_bank(p, mat, M) {
  const g = G();
  const count = Math.max(1, Math.round(n(p.panels, 20)));
  const ph = n(p.panel_h, 3), pw = n(p.panel_w, 0.5), pt = n(p.panel_t, 0.014);
  const inst = new THREE.InstancedMesh(BX(pw, ph, pt), mat, count);
  const m4 = new THREE.Matrix4();
  for (let j = 0; j < count; j++) m4.makeTranslation(0, ph / 2, n(p.gap0, 0.5) + j * n(p.pitch, 0.048)), inst.setMatrixAt(j, m4);
  inst.instanceMatrix.needsUpdate = true;
  inst.castShadow = true;
  inst.receiveShadow = true;
  g.add(inst);
  for (const y of p.header_ys || []) {
    g.add(rod(V3(0, y, 0), V3(0, y, n(p.header_len, 2.4)), n(p.header_r, 0.075), mat));
    g.add(along(mesh(CY(n(p.valve_r, 0.13), n(p.valve_len, 0.07), 16), M.steel || mat, 0, y, n(p.valve_z, 0.14)), "z"));
  }
  if (p.side_plate_t) for (const dx of [-p.side_plate_dx, p.side_plate_dx])
    g.add(mesh(BX(p.side_plate_t, n(p.side_plate_h, ph), n(p.side_plate_d, 1.9)), mat, dx, ph / 2, n(p.side_plate_z, 1.4)));
  return anchors(g, { base: [0, 0, 0], mount: [0, 0, 0], tip: [0, ph, n(p.gap0, 0.5)] });
}

export function fan(p, mat, M) {
  const g = G();
  const r = n(p.r, 0.37), depth = n(p.depth, 0.24);
  const dark = M.dark || mat;
  g.add(mesh(CY(r, depth, 28, undefined, true), mat));
  g.add(mesh(CY(n(p.hub_r, 0.09), depth + 0.02, 14), dark));
  const blades = Math.max(2, Math.round(n(p.blades, 3)));
  for (let k = 0; k < blades; k++) {
    const bl = mesh(BX(r * 0.81, 0.02, r * 0.27), dark);
    bl.rotation.y = (k * 2 * Math.PI) / blades;
    bl.translateX(r * 0.46);
    bl.rotation.x = 0.35;
    g.add(bl);
  }
  g.add(mesh(BX(n(p.guard_w, r * 2), 0.025, 0.03), dark, 0, depth / 2, 0));
  g.add(rod(V3(0, depth / 2, 0), V3(0, depth / 2 + n(p.stem, 0.3), 0), 0.02, M.steel || dark, 6));
  return anchors(g, { base: [0, -depth / 2, 0], top: [0, depth / 2 + n(p.stem, 0.3), 0] });
}

export function conservator(p, mat, M) {
  const g = G();
  const r = n(p.r, 1), len = n(p.len, 4), seg = n(p.segments, 40);
  g.add(along(mesh(CY(r, len, seg), mat), "z"));
  for (const s of [1, -1]) {
    const d = mesh(new THREE.SphereGeometry(r, seg, 12, 0, Math.PI * 2, 0, Math.PI / 2), mat, 0, 0, s * (len / 2));
    d.scale.set(1, n(p.dome_ratio, 0.28), 1);
    d.rotation.x = s > 0 ? Math.PI / 2 : -Math.PI / 2;
    g.add(d);
  }
  if (p.gauge) g.add(along(mesh(CY(n(p.gauge_r, 0.17), n(p.gauge_t, 0.07), 24), M.gauge || mat,
    p.gauge[0], p.gauge[1], p.gauge[2]), "z"));
  return anchors(g, { base: [0, -r, 0], top: [0, r * (1 + n(p.dome_ratio, 0.28)), 0], tip: [0, r, 0] });
}

export function oltc_head(p, mat, M) {
  const g = G();
  const r = n(p.r, 0.34), h = n(p.h, 0.3);
  g.add(mesh(CY(r, h, 28), mat, 0, h / 2, 0));
  g.add(mesh(CY(r * 1.06, n(p.dome_t, 0.04), 28), M.tankD || mat, 0, h + n(p.dome_t, 0.04) / 2, 0));
  const b = p.box || [0.2, 0.14, 0.2];
  g.add(mesh(BX(b[0], b[1], b[2]), M.steel || mat, 0, h + n(p.dome_t, 0.04) + b[1] / 2, 0));
  if (p.inner_h) {
    const top = n(p.inner_top, -0.3);
    g.add(mesh(CY(n(p.inner_r, r), p.inner_h, 24), M.oltcIn || mat, 0, top - p.inner_h / 2, 0));
  }
  return anchors(g, { base: [0, 0, 0], top: [0, h + n(p.dome_t, 0.04) + b[1], 0], tip: [0, h + 0.4, 0] });
}

export function motor_drive(p, mat, M) {
  const g = G();
  const w = n(p.w, 0.45), h = n(p.h, 1.4), d = n(p.d, 0.8);
  g.add(mesh(BX(w, h, d), mat, 0, h / 2, 0));
  g.add(mesh(BX(0.02, h * 0.9, 0.012), M.dark || mat, w / 2 + 0.005, h / 2, 0));
  return anchors(g, { base: [0, 0, 0], top: [0, h, 0] });
}

export function buchholz(p, mat, M) {
  const g = G();
  const w = n(p.w, 0.38), h = n(p.h, 0.3), d = n(p.d, 0.28);
  g.add(mesh(BX(w, h, d), mat));
  g.add(along(mesh(CY(n(p.gauge_r, 0.07), 0.02, 16), M.gauge || mat, 0, 0, d / 2 + 0.01), "z"));
  return anchors(g, { base: [0, -h / 2, 0], top: [0, h / 2, 0] });
}

export function breather(p, mat, M) {
  const g = G();
  const bh = n(p.body_h, 0.6);
  g.add(mesh(CY(n(p.body_r, 0.14), bh, 20), mat, 0, bh / 2, 0));
  g.add(mesh(CY(n(p.cap_r, 0.15), n(p.cap_t, 0.05), 20), M.metal || mat, 0, bh + n(p.cap_t, 0.05) / 2, 0));
  g.add(mesh(CY(n(p.bowl_r, 0.1), n(p.bowl_h, 0.16), 16), M.metal || mat, 0, -n(p.bowl_h, 0.16) / 2, 0));
  return anchors(g, { base: [0, -n(p.bowl_h, 0.16), 0], top: [0, bh + n(p.cap_t, 0.05), 0] });
}

export function prd(p, mat, M) {
  const g = G();
  const h = n(p.h, 0.22);
  g.add(mesh(CY(n(p.r, 0.14), h, 20), mat, 0, h / 2, 0));
  g.add(mesh(BX(n(p.cap_r, 0.36), n(p.cap_t, 0.06), n(p.cap_r, 0.36)), M.tankD || mat, 0, h + n(p.cap_t, 0.06) / 2, 0));
  return anchors(g, { base: [0, 0, 0], top: [0, h + n(p.cap_t, 0.06), 0] });
}

export function roller(p, mat) {
  const g = G();
  g.add(mesh(BX(n(p.bracket_w, 0.46), n(p.bracket_h, 0.14), n(p.bracket_d, 0.32)), mat, 0, n(p.r, 0.16) * 0.69, 0));
  g.add(along(mesh(CY(n(p.r, 0.16), n(p.width, 0.12), 20), mat), "z"));
  return anchors(g, { base: [0, -n(p.r, 0.16), 0], mount: [0, n(p.bracket_h, 0.14), 0] });
}

export function lifting_lug(p, mat) {
  const g = G();
  g.add(mesh(BX(n(p.w, 0.34), n(p.h, 0.45), n(p.t, 0.06)), mat));
  return anchors(g, { base: [0, -n(p.h, 0.45) / 2, 0], tip: [0, n(p.h, 0.45) / 2, 0] });
}

export function jacking_pad(p, mat) {
  const g = G();
  g.add(mesh(BX(n(p.w, 0.34), n(p.h, 0.22), n(p.d, 0.45)), mat));
  return anchors(g, { base: [0, -n(p.h, 0.22) / 2, 0] });
}

export function rail_beam(p, mat, M) {
  const g = G();
  const h = n(p.height, 0.92);
  g.add(mesh(BX(n(p.length, 10), h, n(p.width, 0.6)), mat, 0, h / 2, 0));
  const rh = n(p.rail_h, 0.08);
  g.add(mesh(BX(n(p.length, 10), rh, n(p.rail_w, 0.08)), M.rail || mat, 0, n(p.rail_top, h) - rh / 2, 0));
  return anchors(g, { base: [0, 0, 0], top: [0, n(p.rail_top, h), 0] });
}

export function oil_pit(p, mat, M) {
  const g = G();
  const l = n(p.l, 14), w = n(p.w, 11), kh = n(p.kerb_h, 0.35), kt = n(p.kerb_t, 0.25);
  g.add(mesh(BX(l, n(p.gravel_t, 0.05), w), mat, 0, n(p.gravel_t, 0.05) / 2, 0));
  const c = M.concrete || mat;
  for (const s of [1, -1]) g.add(mesh(BX(n(p.kerb_l, l + 0.5), kh, kt), c, 0, kh / 2, s * (w / 2 + kt / 2)));
  for (const s of [1, -1]) g.add(mesh(BX(kt, kh, n(p.kerb_w, w + 0.45)), c, s * (l / 2 + kt / 2), kh / 2, 0));
  return anchors(g, { base: [0, 0, 0], top: [0, kh, 0] });
}

export function plinth(p, mat) {
  const g = G();
  const h = n(p.height, 0.3);
  g.add(mesh(BX(n(p.length, 2), h, n(p.width, 2)), mat, 0, h / 2, 0));
  return anchors(g, { base: [0, 0, 0], top: [0, h, 0] });
}

/** Cantilever bracket carrying a conservator off the tank end wall. */
export function bracket(p, mat) {
  const g = G();
  const h = n(p.h, 2.7), pw = n(p.post_w, 0.16), pd = n(p.post_d, 0.2);
  g.add(mesh(BX(pw, h, pd), mat, 0, h / 2, 0));
  for (const y of [h * 0.11, h * 0.625]) g.add(mesh(BX(0.12, 0.25, pd * 1.5), mat, -pw / 2 - 0.01, y, 0));
  g.add(mesh(BX(n(p.arm_len, 2.2), 0.16, 0.22), mat, n(p.arm_x, 1.05), n(p.arm_y, 2.64), 0));
  g.add(rod(V3(0, h * 0.22, 0), V3(n(p.arm_len, 2.2) * 0.86, n(p.arm_y, 2.64) - 0.08, 0), n(p.brace_r, 0.065), mat, 8));
  if (p.saddle_x != null) g.add(mesh(BX(n(p.saddle_w, 1.3), 0.2, 0.26), mat, p.saddle_x, n(p.arm_y, 2.64) + 0.18, 0));
  return anchors(g, { base: [0, 0, 0], top: [n(p.arm_x, 1.05), n(p.arm_y, 2.64) + 0.08, 0] });
}

export const TRANSFORMER = {
  tank, bushing, core_3limb, core_5limb, winding, radiator_bank, fan, conservator,
  oltc_head, motor_drive, buchholz, breather, prd, roller, lifting_lug, jacking_pad,
  rail_beam, oil_pit, plinth, bracket,
};
